import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, } from '@angular/material/dialog';
import { TAccountRegion, TBrickSale, TCompetitorSale, TRegionBrick } from '@shared/interfaces/TProject';
import { computeMomentumLong, computeMomentumShort } from '@shared/utils/computeMomentum';
import { isNullOrUndefined } from '@shared/utils/isNullOrUndefined';
import { csvToJson } from 'src/app/helpers/csv-to-json.helper';
import { KamReportsService } from 'src/app/pages/report/report-page/components/kam-reports/services/kam-reports.service';
import { LoadingService } from 'src/app/services/loading.service';
import { MomentumService } from 'src/app/services/momentum.service';
import { TBaseSettingsTabParams } from '../project-settings.component';

@Component({
  selector: 'app-bricks',
  templateUrl: './bricks.component.html',
  styleUrls: ['./bricks.component.scss'],
})
export class BricksComponent implements OnInit {
  public params: TBaseSettingsTabParams; selectedRegionId: string | null = null;
  public regions: TAccountRegion[] = [];
  public bricks: TRegionBrick[] = [];
  public filteredBricks: TRegionBrick[] = [];
  panelOpenState: boolean[] = [];

  public newBrickData: TRegionBrick = {
    id: '',
    name: '',
    regionId: '',
    sales: [],
    salesCompetitors: [],
    salesMomentumShort: 0,
    salesMomentumLong: 0,
  };

  public newSale: TRegionBrick['sales'][number] = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
    sales: 0,
    patients: 0,
    salesInEuro: 0,
  };

  constructor(
    public dialogRef: MatDialogRef<BricksComponent>,
    @Inject(MAT_DIALOG_DATA) public data: TBaseSettingsTabParams,
    private _momentumService: MomentumService,
    public importBricksDialog: MatDialog,
    private _loadingService: LoadingService,
    private _kamReportsService: KamReportsService,
  ) {
    this.params = data;
  }

  ngOnInit(): void {
    this.regions = this.params.selectedProject?.accountRegions ?? [];
  }

  onRegionChange(): void {
    this.refreshBricks();
  }

  public isValid(): boolean {
    // has name and id
    // name is unique for region
    // id is unique for region and project
    return (
      this.newBrickData.name.trim() !== ''
      && this.newBrickData.id.trim() !== ''
      && !this.bricks.map((b) => b.name).includes(this.newBrickData.name)
      && !this.bricks.map((b) => b.id).includes(this.newBrickData.id)
      && !(this.params.selectedProject?.accountBricks ?? []).map((b) => b.id).includes(this.newBrickData.id)
    );
  }

  addBrick(): void {
    if (this.newBrickData.name.trim() && this.selectedRegionId && this.newBrickData.id.trim()) {
      const newBrick = structuredClone(this.newBrickData);
      newBrick.regionId = this.selectedRegionId;
      if (this.params.selectedProject !== undefined && this.params.selectedProject.accountBricks === undefined) {
        this.params.selectedProject.accountBricks = [newBrick];
      } else {
        this.params.selectedProject?.accountBricks.push(newBrick);
      }
      this.refreshBricks();
    }
  }

  public deleteBrick(brick: TRegionBrick): void {
    if (this.params.selectedProject !== undefined && this.params.selectedProject.accountBricks !== undefined) {
      // const brickIndex: number = this.params.selectedProject.accountBricks.findIndex((b) => b.id === brick.id);
      // this.params.selectedProject.accountBricks.splice(brickIndex, 1);
      this.params.selectedProject.accountBricks = this.params.selectedProject.accountBricks.filter((b) => b.id !== brick.id);
      this.refreshBricks();
    }
  }

  private refreshBricks(): void {
    // clear database, fill missing fields with zeros, should be obsolete after some time
    // if (this.params.selectedProject?.accountBricks) {
    //   this.params.selectedProject.accountBricks = this.fillEmptyBricksFields(this.params.selectedProject.accountBricks || []);
    //   this.params.selectedProject.accountBricks = this.clearBricks(this.params.selectedProject.accountBricks || []);
    // }
    this.bricks = this.params.selectedProject?.accountBricks || [];
    this.filteredBricks = this.params.selectedProject?.accountBricks?.filter((b) => b.regionId === this.selectedRegionId) || [];
  }

  clearBricks(bricks: TRegionBrick[]): TRegionBrick[] {
    // filter out bricks from non existing regions
    return bricks.filter(brick => brick.id && this.regions.some(region => region.id === brick.regionId) );
  }

  fillEmptyBricksFields(bricks: TRegionBrick[]): TRegionBrick[] {
    // fill empty fields with 0
    return bricks.map((brick) => {
      brick.sales.forEach((sale) => {
        if (!sale.sales) sale.sales = 0;
        if (!sale.salesInEuro) sale.salesInEuro = 0;
        if (!sale.patients) sale.patients = 0;
      });
      return brick;
    });
  }

  public onSalesChange(salesVal: number, brick: TRegionBrick): void {
    if (salesVal && salesVal > 0) {
      this.updateMomentum(brick);
    }
  }

  isSaleValid(brick: TRegionBrick): boolean {
    // has year and month
    // year is unique for brick
    // month is unique for brick
    return (
      Boolean(this.newSale.year) &&
      Boolean(this.newSale.month) &&
      !brick.sales.some((s) => s.year === this.newSale.year && s.month === this.newSale.month)
    );
  }

  addSale(brick: TRegionBrick): void {
    if (this.newSale.year && this.newSale.month && this.newSale.sales) {
      const newSale = structuredClone(this.newSale);
      brick.sales.push(newSale);
      this.updateMomentum(brick);
    }
  }

  deleteSale(brick: TRegionBrick, sale: TRegionBrick['sales'][number]): void {
    const saleIndex: number = brick.sales.findIndex(
      (s) => s.year === sale.year && s.month === sale.month
    );
    brick.sales.splice(saleIndex, 1);
    this.updateMomentum(brick);
  }

  /**
   * Handles import of bricks metric (sales, salesInEuro, or patients) from a CSV file.
   * @param e - The file input change event
   * @param metricKey - The metric to import: 'sales', 'salesInEuro', or 'patients'
   */
  public importBricks(e: Event, metricKey: 'sales' | 'salesInEuro' | 'patients'): void {
    // console.log('IMPORT START project', this.params.selectedProject);
    // console.log('IMPORT START bricks', this.bricks);
    const target: HTMLInputElement = <HTMLInputElement>e.target;
    if (!isNullOrUndefined(target.files)) {
      const file = target.files[0];
      const reader: FileReader = new FileReader();
      reader.onload = (event) => {
        const results = event?.target?.result ?? '';
        if (!isNullOrUndefined(results) && typeof results === `string`) {
          // Parse CSV
          const rows = csvToJson(results, ';');
          if (rows.length === 0) {
            this._loadingService.triggerErrorMessage('CSV contains no rows.');
            return
          }
          if (!Object.keys(rows[0]).includes('region_id')) {
            this._loadingService.triggerErrorMessage('CSV contains no region_id.');
            return
          }
          if (!Object.keys(rows[0]).includes('name')) {
            this._loadingService.triggerErrorMessage('CSV contains no brick name.');
            return
          }
          if (!Object.keys(rows[0]).includes('id')) {
            this._loadingService.triggerErrorMessage('CSV contains no brick id.');
            return
          }

          // Find all date columns
          const dateColumns = Object.keys(rows[0]).filter((col) => !['region_id', 'name', 'id'].includes(col));
          console.log('Date columns found:', dateColumns);
          if (dateColumns.length === 0) {
            this._loadingService.triggerErrorMessage('CSV contains no date columns.');
            return
          }

          rows.forEach((row, rowIndex) => {
            this.bricks = this.params.selectedProject?.accountBricks || [];
            // console.log('ROW', rowIndex, row, [...this.bricks]);
            if (!row.region_id || !row.id) return;
            // Find brick
            let brick: TRegionBrick | undefined = this.bricks.find(brick => {
              // console.log('CHECK:', brick.id, typeof brick.id, '===', row.id, typeof row.id, this.bricks);
              const result = String(brick.id) === String(row.id) || brick.name === row.name;
              // console.log(`RESULT of comparison of ${String(brick.id)} === ${ String(row.id)} || ${brick.name} === ${row.name}:`, result);
              return result;
            });
            // console.log('Processing row:', rowIndex, 'Found brick:', brick, 'Of bricks', [...this.bricks], 'With ID:', row.id);
            if (!brick) {
              brick = {
                id: row.id,
                name: row.name || `Brick ${rowIndex + 1}`, // Fallback name
                regionId: row.region_id,
                sales: [],
                salesCompetitors: [],
                salesMomentumShort: 0,
                salesMomentumLong: 0,
              };
              this.bricks.push(brick);
              // console.log('NEW BRICK adding:', brick, [...this.bricks]);
            }

            // For each date column, update or add sale
            dateColumns.forEach(dateCol => {
              const [m, y] = dateCol.split('/');
              const month = parseInt(m, 10);
              const year = parseInt(y, 10);
              if (isNaN(month) || isNaN(year)) return;
              const value = row[dateCol];
              // console.log('VALUE', value, typeof value);
              brick = this.bricks.find(b => b.id === row.id && b.regionId === row.region_id);
              // console.log('ADDING BRICK SALES', brick);
              if (brick) {
                let sale: TBrickSale | undefined = brick.sales.find(s => s.month === month && s.year === year);
                // console.log('ADDING SALES is sale?', sale);
                if (sale) {
                  // console.log('ADDING SALES existing', sale);
                  if (metricKey === 'sales') {
                    sale.sales = (value === undefined || value === '') ? (sale.sales ? sale.sales : 0) : Number(value);
                    if (sale.salesInEuro === undefined) sale.salesInEuro = 0;
                    if (sale.patients === undefined) sale.patients = 0;
                  } else if (metricKey === 'salesInEuro') {
                    sale.salesInEuro = (value === undefined || value === '') ? (sale.salesInEuro ? sale.salesInEuro : 0) : Number(value);
                    if (sale.sales === undefined) sale.sales = 0;
                    if (sale.patients === undefined) sale.patients = 0;
                  } else if (metricKey === 'patients') {
                    sale.patients = (value === undefined || value === '') ? (sale.patients ? sale.patients : 0) : Number(value);
                    if (sale.sales === undefined) sale.sales = 0;
                    if (sale.salesInEuro === undefined) sale.salesInEuro = 0;
                  }
                } else {
                  // if no sale exists for this month/year, create a new one
                  const sale: TBrickSale = {
                    month,
                    year,
                    sales: metricKey === 'sales' ? Number(value) : 0,
                    salesInEuro: metricKey === 'salesInEuro' ? Number(value) : 0,
                    patients: metricKey === 'patients' ? Number(value) : 0,
                  };
                  // console.log('ADDING SALES new', sale);
                  brick.sales.push(sale);
                }
              }
            });
            // Update momentum for the competitor
            this.updateMomentum(brick);
            // Update project bricks
            if (this.params.selectedProject) {
              this.params.selectedProject.accountBricks = [...this.bricks];
            }
          });
        } else {
          console.error('File read error: results is not a string');
          this._loadingService.triggerErrorMessage('File read error: result is not a string');
        }

        // this.refreshBricks();
        // console.log('IMPORT BRICKS END - project', this.params.selectedProject);
        this._loadingService.triggerOkMessage(`Bricks ${metricKey} imported successfully!`);

      };
      reader.readAsText(file);
    }

  }

  // public importBricksSalesCallback(e: Event): void {
  //   const target: HTMLInputElement = <HTMLInputElement>e.target;
  //   if (!isNullOrUndefined(target.files)) {
  //     const reader: FileReader = new FileReader();
  //     reader.onload = (event) => {
  //       const results = event?.target?.result ?? '';
  //       if (!isNullOrUndefined(results) && typeof results === `string`) {
  //         const csvOutput = CSVUtils.csvToArray(results);
  //         this._openImportBricksSalesDialog(csvOutput.headers, e);
  //       }
  //     };
  //     reader.readAsText(target.files[0]);
  //   }
  // }

  // public importBricksSalesInEuroCallback(e: Event): void {
  //   const target: HTMLInputElement = <HTMLInputElement>e.target;
  //   if (!isNullOrUndefined(target.files)) {
  //     const reader: FileReader = new FileReader();
  //     reader.onload = (event) => {
  //       const results = event?.target?.result ?? '';
  //       if (!isNullOrUndefined(results) && typeof results === `string`) {
  //         const csvOutput = CSVUtils.csvToArray(results);
  //         this._openImportBricksSalesInEuroDialog(csvOutput.headers, e);
  //       }
  //     };
  //     reader.readAsText(target.files[0]);
  //   }
  // }

  // public importBricksPatientsCallback(e: Event): void {
  //   const target: HTMLInputElement = <HTMLInputElement>e.target;
  //   if (!isNullOrUndefined(target.files)) {
  //     const reader: FileReader = new FileReader();
  //     reader.onload = (event) => {
  //       const results = event?.target?.result ?? '';
  //       if (!isNullOrUndefined(results) && typeof results === `string`) {
  //         const csvOutput = CSVUtils.csvToArray(results);
  //         this._openImportBricksPatientsDialog(csvOutput.headers, e);
  //       }
  //     };
  //     reader.readAsText(target.files[0]);
  //   }
  // }

  public sortSales(brick: TRegionBrick): any[] {
    return brick.sales.sort((a: TBrickSale, b: TBrickSale) => {
      if (a.year === b.year) {
        return b.month - a.month;
      }
      return b.year - a.year;
    });
  }

  // private _openImportBricksSalesDialog(headers: string[], e: Event): void {
  //   this.importBricksDialog.open<ImportComponent, TImportData>(
  //     ImportComponent,
  //     {
  //       width: '80%',
  //       data: {
  //         headers: headers,
  //         projectId: this.params.selectedProject!.id,
  //         area: ImportArea.RegionBricks,
  //         e,
  //         importCallback: (e, mappings) => this.importBricksSales(e, mappings),
  //       },
  //     }
  //   );
  // }

  // private _openImportBricksSalesInEuroDialog(headers: string[], e: Event): void {
  //   this.importBricksDialog.open<ImportComponent, TImportData>(
  //     ImportComponent,
  //     {
  //       width: '80%',
  //       data: {
  //         headers: headers,
  //         projectId: this.params.selectedProject!.id,
  //         area: ImportArea.RegionBricks,
  //         e,
  //         importCallback: (e, mappings) => this.importBricksSalesInEuro(e, mappings),
  //       },
  //     }
  //   );
  // }

  // private _openImportBricksPatientsDialog(headers: string[], e: Event): void {
  //   this.importBricksDialog.open<ImportComponent, TImportData>(
  //     ImportComponent,
  //     {
  //       width: '80%',
  //       data: {
  //         headers: headers,
  //         projectId: this.params.selectedProject!.id,
  //         area: ImportArea.RegionBricks,
  //         e,
  //         importCallback: (e, mappings) =>
  //           this.importBricksPatients(e, mappings),
  //       },
  //     }
  //   );
  // }

  // private importBricksSales(e: Event, mappings: any): void {
  //   if (this.params.selectedProject !== undefined) {
  //     this._importService.importBricksSales(e, () => this.refreshBricks(), this.params.selectedProject.id, mappings);
  //   }
  // }

  // private importBricksSalesInEuro(e: Event, mappings: any): void {
  //   if (this.params.selectedProject !== undefined) {
  //     this._importService.importBricksSalesInEuro(e, () => this.refreshBricks(), this.params.selectedProject.id, mappings);
  //   }
  // }

  // private importBricksPatients(e: Event, mappings: any): void {
  //   if (this.params.selectedProject !== undefined) {
  //     this._importService.importBricksPatients(e, () => this.refreshBricks(), this.params.selectedProject.id, mappings);
  //   }
  // }


  private updateMomentum(brick: TRegionBrick): void {
    brick.salesMomentumShort = computeMomentumShort(this._kamReportsService.getLastXMonthsTotal([brick], 16, 'sales'));
    brick.salesMomentumLong = computeMomentumLong(this._kamReportsService.getLastXMonthsTotal([brick], 16, 'sales'));
  }

  private sortBrickSalesChronologically(data: TBrickSale[]): TBrickSale[] {
    return [...data].sort((a, b) => {
      const yearA = Number(a.year);
      const yearB = Number(b.year);
      const monthA = Number(a.month);
      const monthB = Number(b.month);

      if (yearA !== yearB) {
        return yearA - yearB;
      }
      return monthA - monthB;
    });
  }

  getCompetitorSaleIndex(saleMonth: number, saleYear: number, competitor: TCompetitorSale): number {
    return competitor.sales.findIndex((sale) => +sale.month === +saleMonth && +sale.year === +saleYear);
  }

  formatMomentum(value: number | undefined): string {
    if (!value) { return '0.00%'; }
    return value.toFixed(2) + '%';
  }
}
